import { MAX_COMPETITORS } from '../../sim/match/index.ts';

/** First names dealt to hot-seat seats. Look-only — sim stays on CompetitorId. */
export const BOY_NAMES = [
  'James',
  'Noah',
  'Oliver',
  'Ethan',
  'Liam',
  'Mason',
  'Caleb',
  'Owen',
  'Henry',
  'Felix',
  'Theo',
  'Miles',
  'Jonah',
  'Elliot',
  'Wesley',
  'Graham',
  'Clark',
  'Ashton',
  'Leo',
  'George',
] as const;

export const GIRL_NAMES = [
  'Jane',
  'Clara',
  'Maya',
  'Elena',
  'Iris',
  'Nora',
  'Hazel',
  'Sadie',
  'Ivy',
  'Luna',
  'Freya',
  'Adele',
  'Cora',
  'Pearl',
  'June',
  'Willa',
  'Esme',
  'Thea',
  'Maren',
  'Bridie',
] as const;

export const COMPETITOR_NAME_POOL: readonly string[] = [...BOY_NAMES, ...GIRL_NAMES];

/** Edited racer names stay short enough for the standings Name column. */
export const MAX_COMPETITOR_NAME_LENGTH = 24;

/** Trim, collapse spaces, strip control characters, and cap the length. */
export function sanitizeCompetitorName(raw: string): string {
  return raw
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_COMPETITOR_NAME_LENGTH);
}

/**
 * A full list of cleaned, distinct names, or null when the caller should deal
 * (or keep Player 1). Comparison ignores case.
 */
export function resolveCompetitorNames(
  passed: readonly string[] | undefined,
  count: number,
): string[] | null {
  const n = Math.min(MAX_COMPETITORS, Math.max(0, Math.round(count)));

  if (!passed || n === 0 || passed.length < n) {
    return null;
  }

  const cleaned = passed.slice(0, n).map((name) => sanitizeCompetitorName(name));

  if (cleaned.some((name) => name.length === 0)) {
    return null;
  }

  const keys = cleaned.map((name) => name.toLocaleLowerCase());

  if (new Set(keys).size !== keys.length) {
    return null;
  }

  return cleaned;
}

function shuffle<T>(items: readonly T[], random: () => number): T[] {
  const next = [...items];

  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const left = next[i];
    const right = next[j];

    if (left === undefined || right === undefined) {
      continue;
    }

    next[i] = right;
    next[j] = left;
  }

  return next;
}

/** Distinct first names for a hot-seat field. Solo keeps the caller’s fallback. */
export function dealCompetitorNames(
  count: number,
  random: () => number = Math.random,
): string[] {
  const n = Math.min(MAX_COMPETITORS, Math.max(0, Math.round(count)));

  if (n === 0) {
    return [];
  }

  return shuffle(COMPETITOR_NAME_POOL, random).slice(0, n);
}

export function competitorPossessive(name: string): string {
  return `${name}'s`;
}
